import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, CornerDownRight, Search, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { isOccupiable } from '@/lib/storageTree'
import { fillFromCapacity, fillOf, fillTone, type FillIndex } from '@/lib/slotMetrics'
import {
  browseChildren,
  INITIAL_ROW_BUDGET,
  containerIds,
  containerTree,
  initialExpansion,
  crumbsFor,
  hasChildren,
  levelForSelection,
  type ContainerNode,
} from '@/lib/storageBrowse'
import { useContainerSearch } from '@/hooks/useStorage'
import {
  STORAGE_NODE_KIND_LABELS,
  type ApiStorageNode,
  type StorageTreeNode,
} from '@/types/storage'
import { useLocationText } from '@/hooks/useStoragePlaces'

const TONE_BAR = {
  empty: 'bg-muted-foreground/20',
  some: 'bg-emerald-400',
  most: 'bg-amber-400',
  full: 'bg-rose-400',
} as const

/**
 * Navigate a storage tree one level at a time.
 *
 * A nested tree grows with the data: a facility with ten dewars of twenty racks
 * is hundreds of rows, and finding a box means scrolling past everything you
 * are not looking for. This shows only the level you are on, so the panel's
 * height depends on how many siblings a node has — never on how big the tree
 * is — and search replaces scrolling for everything else.
 *
 * Each row carries how full it is, which is the question that usually sent
 * someone into the tree in the first place.
 */
export function StorageBrowser({
  nodes,
  rootId,
  selectedId,
  onSelect,
  containerCount,
  onExpand,
  layoutOnly = false,
}: {
  /** Containers only, each carrying the capacity the server worked out. */
  nodes: StorageTreeNode[]
  rootId: string
  selectedId: string | null
  onSelect: (id: string) => void
  /**
   * How many containers the unit holds, when that is more than are loaded.
   *
   * Only the size decisions use it — offering "expand all", and saying how many
   * locations there are. Everything else works off what is in hand.
   */
  containerCount?: number | null
  /**
   * Fetch this branch's children, if the caller is loading the tree lazily.
   *
   * Called for every branch this panel opens — the ones it opens by itself on
   * arrival as well as the ones a person clicks. Omitted by callers that
   * already hold the whole unit, for which every branch is a no-op.
   */
  onExpand?: (id: string) => void
  /**
   * Equipment's configuration view: each node shows how many places it has,
   * not how many are in use — that view does not show what is stored.
   */
  layoutOnly?: boolean
}) {
  const [levelId, setLevelId] = useState(rootId)
  const [query, setQuery] = useState('')
  // Whole structure by default. Drilling a level at a time keeps the panel
  // short, but it hides the shape of the unit and you cannot see where there is
  // room without opening every branch in turn.
  const [mode, setMode] = useState<'tree' | 'browse'>('tree')
  /**
   * What a person opened and closed by hand, kept SEPARATE from what the panel
   * opened by itself.
   *
   * Both have to survive the other changing. The automatic set is recomputed as
   * levels arrive — that is how a small unit ends up fully open when its second
   * level lands — and holding one merged set of ids meant every recompute
   * reopened a branch someone had just closed. Closing is therefore recorded as
   * its own fact rather than as an absence.
   */
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set())
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set())

  // Follow a selection made elsewhere (the slot map, a search hit) so the two
  // panes never disagree about where you are.
  useEffect(() => {
    if (!selectedId) return
    const next = levelForSelection(nodes, selectedId)
    if (next) setLevelId(next)
  }, [selectedId, nodes])

  // Reset when the unit changes, or the browser would sit on a stale branch.
  useEffect(() => setLevelId(rootId), [rootId])

  const crumbs = useMemo(() => crumbsFor(nodes, levelId), [nodes, levelId])
  const children = useMemo(() => browseChildren(nodes, levelId), [nodes, levelId])
  const searching = query.trim().length > 0

  /**
   * Search runs on the SERVER.
   *
   * The panel holds only the levels it has opened, and searching is exactly the
   * case where someone does not know which branch to open — so a filter over
   * what happens to be in memory would answer "nothing matches" for a box two
   * racks away. Containers only, like every list here.
   */
  const search = useContainerSearch(rootId, query)
  const results = search.data ?? []

  const tree = useMemo(() => containerTree(nodes, rootId), [nodes, rootId])
  const allIds = useMemo(() => containerIds(nodes, rootId), [nodes, rootId])

  // Every row's slot count and fill, read off the node rather than counted.
  //
  // The server sends each container's capacity with the tree, which is what
  // lets the tree leave the slots out — and leaving them out is the whole
  // saving, since this panel never lists a slot. Counting them here would mean
  // fetching tens of thousands of rows to add up a few dozen bars, which is
  // exactly what it used to do.
  const fills = useMemo(
    () => new Map(nodes.map((n) => [n.id, fillFromCapacity(n.capacity)])),
    [nodes],
  )

  // Opened as far as it can be WITHOUT filling the DOM.
  //
  // This used to open the whole first level unconditionally, which is right for
  // a dewar of four racks and catastrophic for a freezer of a hundred: each
  // rack holds a hundred boxes, so "just the first level" laid out ten thousand
  // rows before anyone had clicked anything. `initialExpansion` opens level by
  // level only while the result stays small, so a small unit still shows its
  // whole shape and a large one shows its racks and waits.
  //
  // Derived rather than stored, and so recomputed as levels arrive — which is
  // what lets a small unit open its second level once that level exists. It
  // used to be state seeded once per unit, because recomputing it collapsed
  // every branch the user had opened; that is now impossible, since what a
  // person opened and closed is recorded separately below.
  const automatic = useMemo(() => initialExpansion(nodes, rootId), [nodes, rootId])

  const expanded = useMemo(() => {
    const next = new Set([...automatic, ...opened])
    for (const id of closed) next.delete(id)
    return next
  }, [automatic, opened, closed])

  // Fetch what is open. Idempotent for a branch already loaded, and a no-op
  // entirely for a caller holding the whole unit already.
  useEffect(() => {
    if (!onExpand) return
    for (const id of expanded) onExpand(id)
  }, [expanded, onExpand])

  // The level in front of you in browse mode, for the same reason.
  useEffect(() => {
    onExpand?.(levelId)
  }, [levelId, onExpand])

  // Start again when the unit changes, or the panel carries one unit's opened
  // branches into the next.
  useEffect(() => {
    setOpened(new Set())
    setClosed(new Set())
  }, [rootId])

  // Opening everything is only offered when everything can be drawn. On a unit
  // with ten thousand boxes the control was a button whose whole function was
  // to freeze the tab.
  //
  // Measured against the size of the UNIT, not of what is loaded. With a lazy
  // tree those are different numbers, and the one in hand is the smaller of
  // them — which would put the button back on exactly the units it was taken
  // off, and make it fetch ten thousand boxes on the way to freezing.
  const total = containerCount ?? allIds.length
  const canExpandAll = total <= INITIAL_ROW_BUDGET

  const toggle = (id: string) => {
    const isOpen = expanded.has(id)
    setOpened((prev) => {
      const next = new Set(prev)
      if (isOpen) next.delete(id)
      else next.add(id)
      return next
    })
    // Closing has to be recorded, not merely un-opened: the automatic set would
    // otherwise put it straight back the next time a level arrives.
    setClosed((prev) => {
      const next = new Set(prev)
      if (isOpen) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const collapseAll = () => {
    setOpened(new Set())
    setClosed(new Set(allIds))
  }

  const open = (node: ApiStorageNode) => {
    onSelect(node.id)
    if (!isOccupiable(node.kind) && hasChildren(nodes, node.id)) setLevelId(node.id)
  }

  return (
    <div className='space-y-2'>
      {/* Two ways of looking at the same tree: everything at once, or one level
          at a time for a unit too big to take in. A segmented control rather
          than nested Tabs — a second role="tabpanel" breaks the unscoped
          getByRole locators in the e2e suite. */}
      <div className='flex w-fit items-center gap-0.5 rounded-md border bg-muted/40 p-0.5'>
        {(['tree', 'browse'] as const).map((m) => (
          <button
            key={m}
            type='button'
            onClick={() => setMode(m)}
            className={cn(
              'rounded px-2 py-0.5 text-[11px] font-medium transition-colors',
              mode === m
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {m === 'tree' ? 'Full structure' : 'One level'}
          </button>
        ))}
      </div>

      <div className='relative'>
        <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Find a rack or box…'
          className='h-8 pl-8 pr-8 text-sm'
        />
        {searching && (
          <button
            type='button'
            aria-label='Clear search'
            onClick={() => setQuery('')}
            className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
          >
            <X className='h-3.5 w-3.5' />
          </button>
        )}
      </div>

      {searching ? (
        <SearchResults
          results={results}
          loading={search.isPending}
          onPick={(node) => {
            setQuery('')
            onSelect(node.id)
            const next = levelForSelection(nodes, node.id)
            if (next) setLevelId(next)
          }}
        />
      ) : mode === 'tree' ? (
        <>
          <div className='flex items-center justify-between border-b pb-2 text-xs'>
            <span className='text-muted-foreground'>
              {tree.length === 0 ? 'No layout yet' : `${total} location(s)`}
            </span>
            {canExpandAll ? (
              <button
                type='button'
                onClick={() => {
                  if (expanded.size === allIds.length) collapseAll()
                  else {
                    setOpened(new Set(allIds))
                    setClosed(new Set())
                  }
                }}
                className='font-medium text-primary hover:underline'
              >
                {expanded.size === allIds.length ? 'Collapse all' : 'Expand all'}
              </button>
            ) : expanded.size > 0 ? (
              <button
                type='button'
                onClick={collapseAll}
                className='font-medium text-primary hover:underline'
              >
                Collapse all
              </button>
            ) : (
              <span className='text-muted-foreground'>Open a rack to see inside it</span>
            )}
          </div>

          {tree.length === 0 ? (
            <p className='px-1 py-6 text-center text-xs text-muted-foreground'>
              Nothing is described inside this unit yet.
            </p>
          ) : (
            <ul className='max-h-[26rem] space-y-1 overflow-y-auto pr-1'>
              {tree.map((node) => (
                <TreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  fills={fills}
                  selectedId={selectedId}
                  expanded={expanded}
                  onToggle={toggle}
                  onSelect={onSelect}
                  layoutOnly={layoutOnly}
                />
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <Breadcrumb crumbs={crumbs} levelId={levelId} onJump={setLevelId} />

          {children.length === 0 ? (
            <p className='px-1 py-6 text-center text-xs text-muted-foreground'>
              {/* A level that is still arriving is not an empty one. Telling
                  someone their rack is empty while its boxes are in flight is
                  the one wrong answer this panel can give. */}
              {hasChildren(nodes, levelId)
                ? 'Loading…'
                : 'Nothing inside this one — its contents are on the slot map.'}
            </p>
          ) : (
            <ul className='max-h-[22rem] space-y-1 overflow-y-auto pr-1'>
              {children.map((child) => (
                <Row
                  key={child.id}
                  node={child}
                  nodes={nodes}
                  fills={fills}
                  selected={selectedId === child.id}
                  onOpen={() => open(child)}
                  layoutOnly={layoutOnly}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

/**
 * One branch of the full-structure view.
 *
 * A slot is never a row: the count and fill travel on the parent, and clicking
 * the parent puts its grid on the map. A 9×9 box would otherwise add 81 rows
 * to the thing you opened to get an overview.
 */
function TreeRow({
  node,
  depth,
  fills,
  selectedId,
  expanded,
  onToggle,
  onSelect,
  layoutOnly,
}: {
  node: ContainerNode
  depth: number
  fills: FillIndex
  selectedId: string | null
  expanded: ReadonlySet<string>
  onToggle: (id: string) => void
  onSelect: (id: string) => void
  layoutOnly: boolean
}) {
  const fill = fillOf(fills, node.id)
  const slots = fill.total
  const open = expanded.has(node.id)
  const branches = node.children
  // The expander comes off the COUNT, not off the loaded children, so a branch
  // whose level has not been fetched still offers the control that fetches it.
  // Without this a lazily loaded tree hides itself: a hundred racks draw as
  // leaves and nothing can open one.
  const expandable = (node.childCount ?? branches.length) > 0
  const awaitingChildren = open && expandable && branches.length === 0

  return (
    <li>
      <div
        className={cn(
          'flex items-center gap-1.5 rounded-md px-1.5 py-1 text-sm',
          selectedId === node.id ? 'bg-primary/10' : 'hover:bg-muted/60',
        )}
        // Indent stops growing after a few levels. A deep tree in a fixed-width
        // panel otherwise spends the whole row on whitespace, and the code —
        // the only thing that says WHICH rack this is — gets squeezed out.
        style={{ paddingLeft: `${Math.min(depth, 5) * 11 + 6}px` }}
      >
        {expandable ? (
          <button
            type='button'
            aria-label={open ? `Collapse ${node.code}` : `Expand ${node.code}`}
            onClick={() => onToggle(node.id)}
            className='shrink-0 text-muted-foreground hover:text-foreground'
          >
            <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
          </button>
        ) : (
          <span className='w-3.5 shrink-0' />
        )}

        <button
          type='button'
          onClick={() => onSelect(node.id)}
          className='flex min-w-0 flex-1 items-center gap-2 text-left'
        >
          {/* The code never truncates. It is short ("R1", "B3") and it is the
              only thing that says which node this row is; the kind label is
              recoverable from the shape of the tree, so that is what gives way
              when the panel runs out of width. */}
          <span className='shrink-0 font-medium'>{node.code}</span>
          <span className='min-w-0 flex-1 truncate text-[11px] text-muted-foreground'>
            {STORAGE_NODE_KIND_LABELS[node.kind]}
          </span>
          {slots > 0 && layoutOnly && (
            <span className='shrink-0 text-[11px] tabular-nums text-muted-foreground'>{slots} places</span>
          )}
          {slots > 0 && !layoutOnly && (
            <span className='flex shrink-0 items-center gap-1.5'>
              <span className='h-1.5 w-8 overflow-hidden rounded-full bg-muted'>
                <span
                  className={cn('block h-full rounded-full', TONE_BAR[fillTone(fill.pct)])}
                  style={{ width: `${fill.pct}%` }}
                />
              </span>
              <span className='text-right text-[11px] tabular-nums text-muted-foreground'>
                {fill.used}/{slots}
              </span>
            </span>
          )}
        </button>
      </div>

      {awaitingChildren && (
        <p
          className='py-1 text-[11px] text-muted-foreground'
          style={{ paddingLeft: `${Math.min(depth + 1, 5) * 11 + 20}px` }}
        >
          Loading…
        </p>
      )}

      {open && branches.length > 0 && (
        <ul className='space-y-1'>
          {branches.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              fills={fills}
              selectedId={selectedId}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              layoutOnly={layoutOnly}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

function Breadcrumb({
  crumbs,
  levelId,
  onJump,
}: {
  crumbs: ApiStorageNode[]
  levelId: string
  onJump: (id: string) => void
}) {
  const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null

  return (
    <div className='flex items-center gap-1 border-b pb-2 text-xs'>
      {parent && (
        <button
          type='button'
          onClick={() => onJump(parent.id)}
          aria-label='Back'
          className='rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground'
        >
          <ChevronLeft className='h-4 w-4' />
        </button>
      )}
      <nav className='flex min-w-0 flex-wrap items-center gap-1'>
        {crumbs.map((crumb, i) => (
          <span key={crumb.id} className='flex items-center gap-1'>
            {i > 0 && <span className='text-muted-foreground'>›</span>}
            <button
              type='button'
              onClick={() => onJump(crumb.id)}
              className={cn(
                'truncate rounded px-1 py-0.5',
                crumb.id === levelId
                  ? 'font-semibold text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {crumb.code}
            </button>
          </span>
        ))}
      </nav>
    </div>
  )
}

function Row({
  node,
  nodes,
  fills,
  selected,
  onOpen,
  layoutOnly,
}: {
  node: ApiStorageNode
  nodes: ApiStorageNode[]
  fills: FillIndex
  selected: boolean
  onOpen: () => void
  layoutOnly: boolean
}) {
  const fill = fillOf(fills, node.id)
  const drillable = hasChildren(nodes, node.id)
  const slots = fill.total

  return (
    <li>
      <button
        type='button'
        onClick={onOpen}
        className={cn(
          'group flex w-full items-center gap-3 rounded-lg border px-2.5 py-2 text-left transition-colors',
          selected ? 'border-primary/40 bg-primary/5' : 'hover:bg-muted/50',
        )}
      >
        <div className='min-w-0 flex-1'>
          <div className='flex items-baseline gap-2'>
            {/* Same rule as the tree: the code is what identifies the row, so
                the optional descriptive name is what gets clipped. */}
            <span className='shrink-0 text-sm font-medium'>{node.code}</span>
            {node.name && (
              <span className='min-w-0 truncate text-xs text-muted-foreground'>{node.name}</span>
            )}
          </div>
          <div className='mt-1 flex items-center gap-2'>
            {!layoutOnly && (
              <span className='h-1 w-24 overflow-hidden rounded-full bg-muted'>
                <span
                  className={cn('block h-full rounded-full', TONE_BAR[fillTone(fill.pct)])}
                  style={{ width: `${fill.pct}%` }}
                />
              </span>
            )}
            <span className='text-[11px] text-muted-foreground'>
              {slots === 0 ? STORAGE_NODE_KIND_LABELS[node.kind] : layoutOnly ? `${slots} places` : fill.label}
            </span>
          </div>
        </div>

        <Badge variant='outline' className='shrink-0 text-[10px] font-normal'>
          {STORAGE_NODE_KIND_LABELS[node.kind]}
        </Badge>
        {node.isHoldingArea && (
          <Badge variant='outline' className='shrink-0 border-amber-400 text-[10px] text-amber-700'>
            unassigned
          </Badge>
        )}
        {drillable && (
          <ChevronRight className='h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground' />
        )}
      </button>
    </li>
  )
}

function SearchResults({
  results,
  loading,
  onPick,
}: {
  results: ApiStorageNode[]
  /** The answer comes from the server now, so "no results" has to wait for it. */
  loading: boolean
  onPick: (node: ApiStorageNode) => void
}) {
  const locate = useLocationText()
  if (loading) {
    return <p className='px-1 py-6 text-center text-xs text-muted-foreground'>Searching…</p>
  }

  if (results.length === 0) {
    return <p className='px-1 py-6 text-center text-xs text-muted-foreground'>Nothing matches.</p>
  }

  return (
    <ul className='max-h-[24rem] space-y-0.5 overflow-y-auto pr-1'>
      {results.map((node) => (
        <li key={node.id}>
          <button
            type='button'
            onClick={() => onPick(node)}
            className='flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/60'
          >
            <CornerDownRight className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
            <span className='font-medium'>{node.code}</span>
            <span className='truncate text-xs text-muted-foreground'>
              {locate(node.path)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
